import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useConsulting } from '../../context/ConsultingContext';
import { useOnboarding } from '../../context/OnboardingContext';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import { Header, Footer } from '../../components/layout';
import ConsultantSelector from '../../components/consultant/ConsultantSelector';
import { OnboardingTour, OnboardingWelcomeModal, MobileOnboardingGuide } from '../../components/onboarding';
import { dashboardSteps, mobileOnboardingSteps } from '../../utils/onboardingSteps';
import {
  HeroSection,
  OutputTypesSection,
  DifferentiationSection,
  TestimonialsSection,
  VideoSection,
  SloganSection,
  CTASection,
  NameModal
} from '../../components/dashboard';
import { SEOHelmet } from '../../components/common';
import '../../styles/desktop/DashboardPage.css';
import '../../styles/mobile/mobile-dashboard.css';
import '../../styles/components/MobileOnboarding.css';

const DashboardPage = () => {
  const { user, profile, hasPromoCode, isAdmin } = useAuth();
  const { startNewSession, currentSession, loading, prepareSessionForOnboarding } = useConsulting();
  const { 
    isOnboardingActive, 
    shouldShowWelcome, 
    startOnboarding, 
    completeOnboarding, 
    skipOnboarding,
    currentStep,
    activePage 
  } = useOnboarding();
  const { isAdmin: adminFromHook, adminCheckLoading } = useAdminCheck();
  const navigate = useNavigate();
  const [showNameModal, setShowNameModal] = useState(false);
  const [name, setName] = useState('');
  const [showConsultantSelector, setShowConsultantSelector] = useState(false);
  const [selectedConsultantData, setSelectedConsultantData] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // 모바일 감지
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // 프로모션 코드 체크 함수
  const checkPromoCodeAndRedirect = () => {
    // 로그인한 사용자이고, 프로모션 코드 확인이 완료되었을 때
    if (user && hasPromoCode !== null) {
      // 관리자가 아니고 프로모션 코드가 없는 경우
      if (!isAdmin && !hasPromoCode) {
        console.log('프로모션 코드가 없는 사용자 - /profile로 리다이렉트');
        navigate('/profile', { replace: false, state: { needPromoCode: true } });
        return false; // 진행 불가
      }
    }
    return true; // 진행 가능
  };

  const handleStartOnboarding = async () => {
    try {
      const userName = profile?.name || user?.user_metadata?.full_name || user?.user_metadata?.name || '';
      await prepareSessionForOnboarding(userName);
      startOnboarding('dashboard');
    } catch (error) {
      console.error('onboarding start failed:', error);
      startOnboarding('dashboard');
    }
  };

  const handleSkipOnboarding = () => {
    skipOnboarding();
  };

  const handleOnboardingComplete = async () => {
    if (currentStep === 'dashboard') {
      startOnboarding('consulting');
      await handleStartOnboardingConsulting();
    } else {
      completeOnboarding();
    }
  };

  const handleStartOnboardingConsulting = async () => {
    try {
      const userName = profile?.name || user?.user_metadata?.full_name || user?.user_metadata?.name || '';
      await prepareSessionForOnboarding(userName);
      navigate('/consulting');
    } catch (error) {
      console.error('onboarding consulting start failed:', error);
      alert('컨설팅 세션을 준비하는 중 오류가 발생했습니다.');
    }
  };

  const handleContinueConsulting = () => {
    // 로그인하지 않은 사용자는 로그인 페이지로 이동
    if (!user) {
      navigate('/login');
      return;
    }
    
    // 프로모션 코드 체크
    if (!checkPromoCodeAndRedirect()) {
      return;
    }
    
    navigate('/consulting');
  };

  const handleStartNewConsulting = async () => {
    // 로그인하지 않은 사용자는 로그인 페이지로 이동
    if (!user) {
      navigate('/login');
      return;
    }
    
    // 프로모션 코드 체크
    if (!checkPromoCodeAndRedirect()) {
      return;
    }
    
    // 먼저 컨설턴트 선택 모달을 표시
    setShowConsultantSelector(true);
  };

  const handleConsultantSelect = async (consultantData) => {
    setSelectedConsultantData(consultantData);
    setShowConsultantSelector(false);
    
    // 프로필에서 이름이 있으면 바로 세션 시작, 없으면 이름 입력 모달 표시
    if (profile?.name) {
      try {
        await startNewSession(profile.name, consultantData);
        if (isOnboardingActive) {
          startOnboarding('consulting');
        }
        navigate('/consulting');
      } catch (error) {
        console.error('세션 시작 실패:', error);
        alert('컨설팅 세션을 시작하는 중 오류가 발생했습니다.');
      }
    } else {
      setShowNameModal(true);
    }
  };

  const handleConsultantSelectorCancel = () => {
    setShowConsultantSelector(false);
    setSelectedConsultantData(null);
  };

  const handleNameSubmit = async () => {
    if (name.trim() && selectedConsultantData) {
      try {
        await startNewSession(name.trim(), selectedConsultantData);
        if (isOnboardingActive) {
          startOnboarding('consulting');
        }
        navigate('/consulting');
        setShowNameModal(false);
        setName('');
        setSelectedConsultantData(null);
      } catch (error) {
        console.error('세션 시작 실패:', error);
        alert('컨설팅 세션을 시작하는 중 오류가 발생했습니다.');
      }
    }
  };

  const handleNameModalClose = () => {
    setShowNameModal(false);
    setName('');
    setSelectedConsultantData(null);
  };

  const displayName = profile?.name || user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || '사용자';

  return (
    <>
      <SEOHelmet 
        title="AI 이력서 컨설팅 플랫폼"
        description="AI를 활용한 이력서 컨설팅 플랫폼. 라떼 한 잔의 시간 동안 AI 컨설턴트와 음성으로 편하게 대화하면서 전문 이력서를 만들어보세요."
        keywords="AI 이력서, 음성 컨설팅, 커리어 컨설팅, PDF 이력서, 영어 이력서, 웹 포트폴리오"
        url="/"
        type="website"
      />
      <div className="page-container-white mobile-fixed-layout">
      <Header />
      
      <main>
        <HeroSection
          displayName={displayName}
          loading={loading}
          currentSession={currentSession}
          onContinueConsulting={handleContinueConsulting}
          onStartNewConsulting={handleStartNewConsulting}
        />
        
        <DifferentiationSection />
        
        <OutputTypesSection />
        
        <TestimonialsSection />
        
        <VideoSection />
        
        {/* <SloganSection /> */}
        
        <CTASection
          currentSession={currentSession}
          onContinueConsulting={handleContinueConsulting}
          onStartNewConsulting={handleStartNewConsulting}
        />
      </main>
      
      <Footer />
      
      <OnboardingWelcomeModal
        isOpen={shouldShowWelcome}
        onStart={handleStartOnboarding}
        onSkip={handleSkipOnboarding}
        userName={displayName}
      />
      
      {isOnboardingActive && currentStep === 'dashboard' && activePage === 'dashboard' && (
        isMobile ? (
          <MobileOnboardingGuide
            isActive={true}
            steps={mobileOnboardingSteps}
            onComplete={handleOnboardingComplete}
            onSkip={handleSkipOnboarding}
          />
        ) : (
          <OnboardingTour
            isActive={true}
            steps={dashboardSteps}
            currentStepIndex={0}
            onComplete={handleOnboardingComplete}
            onSkip={handleSkipOnboarding}
          />
        )
      )}
      
      {/* 컨설턴트 선택 모달 */}
      {showConsultantSelector && (
        <ConsultantSelector
          onSelectConsultant={handleConsultantSelect}
          onCancel={handleConsultantSelectorCancel}
        />
      )}
      
      <NameModal
        isOpen={showNameModal}
        name={name}
        onNameChange={setName}
        onSubmit={handleNameSubmit}
        onClose={handleNameModalClose}
        loading={loading}
      />
      </div>
    </>
  );
};

export default DashboardPage;
